import { useState, useEffect } from 'react';
import { FaTrash, FaImage, FaSpinner, FaPlus, FaVolumeUp, FaLanguage, FaCode, FaList } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const createEmptyJoinWord = () => ({
  leftWord: '',
  rightWord: '',
  answer: '',
  answerMeaning: '',
  confusingLetters: '',
  leftWordAudio: null,
  rightWordAudio: null,
  answerAudio: null,
  image: null
});

const transformImportedJson = (data) => {
  // Check if it's the "grammar" exercise format
  if (data?.type === 'grammar' && Array.isArray(data.items)) {
    return {
      words: data.items.map(item => {
        const question = item.question || '';
        // Handle "Left + Right =" format
        let leftWord = '';
        let rightWord = '';
        
        if (question.includes('+')) {
          const parts = question.split('+');
          leftWord = parts[0]?.trim() || '';
          const rightAndEqual = parts[1]?.split('=');
          rightWord = rightAndEqual[0]?.trim() || '';
        }

        return {
          leftWord,
          rightWord,
          answer: item.answer || '',
          answerMeaning: item.englishTranslation || '',
          confusingLetters: item.keywords?.[0]?.confusingLetters?.join(' ') || '',
          leftWordAudio: null,
          rightWordAudio: null,
          answerAudio: null,
          image: null
        };
      })
    };
  }
  
  // Also handle the case where the whole object is the exercise list
  if (Array.isArray(data?.exercises)) {
    const joinWordExercise = data.exercises.find(e => e.type === 'grammar' && e.title?.includes('ಕೂಡಿಸಿ'));
    if (joinWordExercise) return transformImportedJson(joinWordExercise);
  }

  return data;
};

const JoinWordsActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [createEmptyJoinWord()]
  });
  const [activeTab, setActiveTab] = useState('form'); // 'form' or 'json'
  const [jsonInput, setJsonInput] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  const buildStoragePath = ({ wordId, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'join_words',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        let parsed = JSON.parse(initialContent);
        // Apply transformation if needed
        parsed = transformImportedJson(parsed);
        
        const validatedWords = {
          words: Array.isArray(parsed?.words) && parsed.words.length > 0
            ? parsed.words.map(word => ({ ...createEmptyJoinWord(), ...word }))
            : [createEmptyJoinWord()]
        };
        setContent(validatedWords);
        setJsonInput(JSON.stringify(validatedWords, null, 2));
      } catch {
        setContent({ words: [createEmptyJoinWord()] });
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
    setJsonInput(JSON.stringify(content, null, 2));
  }, [content, onContentChange]);

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonInput(value);
    try {
      let parsed = JSON.parse(value);
      // Apply transformation if needed
      parsed = transformImportedJson(parsed);
      
      if (parsed && Array.isArray(parsed.words)) {
        setContent(parsed);
      }
    } catch {
      // Invalid JSON, don't update content yet
    }
  };

  const addWord = () => {
    setContent(prev => ({
      ...prev,
      words: [...prev.words, createEmptyJoinWord()]
    }));
  };

  const removeWord = async (index) => {
    if (content.words.length <= 1) return;

    const wordToDelete = content.words[index];
    const storage = getStorage();
    const fileTypes = ['leftWordAudio', 'rightWordAudio', 'answerAudio', 'image'];

    for (const fileType of fileTypes) {
      if (wordToDelete[fileType]) {
        try {
          const fileRef = ref(storage, wordToDelete[fileType]);
          await deleteObject(fileRef);
        } catch {
          // Ignore cleanup failures
        }
      }
    }

    setContent(prev => ({
      ...prev,
      words: prev.words.filter((_, i) => i !== index)
    }));
  };

  const updateWord = (index, field, value) => {
    const updatedWords = [...content.words];
    updatedWords[index] = { ...updatedWords[index], [field]: value };
    setContent(prev => ({ ...prev, words: updatedWords }));
  };

  const handleFileUpload = async (index, fileType, file) => {
    if (!file) return;

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const wordId = `word${index + 1}`;
      const storagePath = buildStoragePath({ wordId, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const oldPath = content.words[index][fileType];
      if (oldPath) {
        try {
          const oldRef = ref(storage, oldPath);
          await deleteObject(oldRef);
        } catch {
          // Ignore cleanup failures
        }
      }

      updateWord(index, fileType, storagePath);
    } catch (error) {
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play();
    } catch {
      alert('Failed to play audio');
    }
  };

  const translateAllEnglish = async () => {
    setTranslating(true);
    try {
      const storage = getStorage();
      const translationTargets = [];

      content.words.forEach((word, index) => {
        if (word.answer?.trim() && !word.answerMeaning?.trim()) {
          translationTargets.push({ index, field: 'answerMeaning', sourceText: word.answer });
        }
      });

      if (translationTargets.length === 0) {
        alert('All items already have English meanings or no text to translate.');
        return;
      }

      const translatedTexts = await translateBatchToText(
        translationTargets.map(item => item.sourceText),
        db,
        5,
        2000,
        Constants.LANGUAGES.KANNADA,
        Constants.LANGUAGES.ENGLISH
      );

      const updatedWords = [...content.words];
      translationTargets.forEach((target, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedWords[target.index][target.field] = translatedText;
        }
      });

      setContent({ ...content, words: updatedWords });
      alert('Translation completed!');
    } catch (error) {
      alert('Failed to translate words: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  const generateAllAudio = async () => {
    setGeneratingAudio(true);
    try {
      const storage = getStorage();
      const requests = [];

      content.words.forEach((word, index) => {
        if (word.leftWord?.trim() && !word.leftWordAudio) {
          requests.push({ index, field: 'leftWordAudio', text: word.leftWord, languageCode: LanguageCodes.KANNADA });
        }
        if (word.rightWord?.trim() && !word.rightWordAudio) {
          requests.push({ index, field: 'rightWordAudio', text: word.rightWord, languageCode: LanguageCodes.KANNADA });
        }
        if (word.answer?.trim() && !word.answerAudio) {
          requests.push({ index, field: 'answerAudio', text: word.answer, languageCode: LanguageCodes.KANNADA });
        }
        if (word.answerMeaning?.trim() && !word.englishAudio) {
          requests.push({ index, field: 'englishAudio', text: word.answerMeaning, languageCode: LanguageCodes.ENGLISH });
        }
      });

      if (requests.length === 0) {
        alert('No new audio needs to be generated.');
        return;
      }

      const audioUrls = await generateBatchSpeechAudio(
        requests.map(request => ({
          text: request.text,
          languageCode: request.languageCode,
          ssmlGender: VoiceGenders.FEMALE
        })),
        db,
        storage
      );

      // Deep clone the words array to ensure React detects property changes
      const updatedWords = content.words.map(w => ({ ...w }));
      
      requests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          updatedWords[request.index][request.field] = audioUrl;
        }
      });

      setContent(prev => ({ ...prev, words: updatedWords }));
      alert(`Successfully generated ${requests.length} audio files!`);
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          className={`px-6 py-3 font-medium text-sm flex items-center gap-2 transition-colors ${
            activeTab === 'form'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => setActiveTab('form')}
        >
          <FaList className="w-4 h-4" />
          Form Editor
        </button>
        <button
          className={`px-6 py-3 font-medium text-sm flex items-center gap-2 transition-colors ${
            activeTab === 'json'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => setActiveTab('json')}
        >
          <FaCode className="w-4 h-4" />
          JSON Editor
        </button>
      </div>

      {activeTab === 'json' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <textarea
            className="w-full h-[500px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            value={jsonInput}
            onChange={handleJsonChange}
            placeholder="Paste valid JSON here..."
          />
          {(() => {
            try {
              JSON.parse(jsonInput);
              return <p className="mt-2 text-xs text-green-600">✓ Valid JSON</p>;
            } catch (e) {
              return <p className="mt-2 text-xs text-red-600">✗ Invalid JSON: {e.message}</p>;
            }
          })()}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Join Words</h3>
              <p className="text-sm text-gray-600 mt-1">{content.words.length} items configured</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={translateAllEnglish}
                disabled={translating || generatingAudio}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm disabled:bg-purple-400"
              >
                {translating ? <FaSpinner className="animate-spin" /> : <FaLanguage className="mr-2" />}
                Translate
              </button>
              <button
                type="button"
                onClick={generateAllAudio}
                disabled={generatingAudio || translating}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm disabled:bg-emerald-400"
              >
                {generatingAudio ? <FaSpinner className="animate-spin" /> : <FaVolumeUp className="mr-2" />}
                Audio
              </button>
              <button
                type="button"
                onClick={addWord}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                <FaPlus className="mr-2" />
                Add Item
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {content.words.map((word, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {index + 1}
                  </div>

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Left Word *"
                      value={word.leftWord || ''}
                      onChange={(e) => updateWord(index, 'leftWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Right Word *"
                      value={word.rightWord || ''}
                      onChange={(e) => updateWord(index, 'rightWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Answer *"
                      value={word.answer || ''}
                      onChange={(e) => updateWord(index, 'answer', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Answer Meaning"
                      value={word.answerMeaning || ''}
                      onChange={(e) => updateWord(index, 'answerMeaning', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm lg:col-span-2"
                      placeholder="Confusing Letters (space-separated) *"
                      value={word.confusingLetters || ''}
                      onChange={(e) => updateWord(index, 'confusingLetters', e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    {[['leftWordAudio', 'W1'], ['rightWordAudio', 'W2'], ['answerAudio', 'ANS']].map(([field, label]) => (
                      <div key={field} className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`${field}-${index}`}
                          onChange={(e) => handleFileUpload(index, field, e.target.files[0])}
                          disabled={uploadingFiles[`${index}-${field}`]}
                        />
                        <label htmlFor={`${field}-${index}`} className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100">
                          🎵 {label}
                        </label>
                        {word[field] && (
                          <button type="button" className="p-1 text-green-600" onClick={() => playAudio(word[field])}>
                            <FaVolumeUp className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`image-${index}`}
                        onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                      />
                      <label htmlFor={`image-${index}`} className="cursor-pointer px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs hover:bg-pink-100">
                        🖼️ IMG
                      </label>
                      {word.image && (
                        <button type="button" className="p-1 text-blue-600" onClick={async () => {
                          const url = await getDownloadURL(ref(getStorage(), word.image));
                          window.open(url, '_blank');
                        }}>
                          <FaImage className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {content.words.length > 1 && (
                    <button type="button" className="p-2 text-red-600 hover:bg-red-100 rounded" onClick={() => removeWord(index)}>
                      <FaTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const validateJoinWordsContent = (content) => {
  const errors = [];
  if (!content?.words || !Array.isArray(content.words) || content.words.length === 0) {
    errors.push('At least one word item is required');
    return errors;
  }
  content.words.forEach((word, index) => {
    if (!word.leftWord?.trim()) errors.push(`Item ${index + 1}: Left word is required`);
    if (!word.rightWord?.trim()) errors.push(`Item ${index + 1}: Right word is required`);
    if (!word.answer?.trim()) errors.push(`Item ${index + 1}: Answer is required`);
    if (!word.confusingLetters?.trim()) errors.push(`Item ${index + 1}: Confusing letters are required`);
    if (word.answerMeaning?.trim() && !/^[A-Z]/.test(word.answerMeaning.trim())) {
      errors.push(`Item ${index + 1}: Answer meaning must start with a capital letter`);
    }
  });
  return errors;
};

export default JoinWordsActivity;
